import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  CreditCard,
  DollarSign,
  List,
  RefreshCw,
  Settings,
  TrendingUp,
  Users,
} from 'lucide-react';

import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { customers } from '../../services/api/admin';
import { get } from '../../services/api/client';

import CockpitHome from './CockpitHome';
import { CockpitPanel, CockpitTabs, CockpitWorkspaceContent } from './cockpit';
import { adminRoutes } from './navigation/adminRoutes';
import { getRememberedCockpitPath } from './navigation/adminPlaceMemory';
import { canAccessSection, getAdminUser } from './navigation/sectionAccess';

const SUBSCRIPTIONS_PATH = '/admin/students/customers';
const STRIPE_SETTINGS_PATH = '/admin/settings/stripe';
const ACTIVITY_PERIOD_DAYS = 30;
const DAY_IN_MS = 24 * 60 * 60 * 1000;

const isOnOrAfter = (value, threshold) => {
  if (!value) return false;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) && timestamp >= threshold;
};

export const calculateFinanceMetrics = (customerList = [], now = new Date()) => {
  const periodStart = now.getTime() - (ACTIVITY_PERIOD_DAYS * DAY_IN_MS);
  const activeSubscriptions = customerList.filter(
    (customer) => customer.subscription_status === 'active'
  );
  const paymentIssues = customerList.filter(
    (customer) => customer.subscription_status === 'past_due'
      || customer.content_access_auto_reason === 'payment_failed'
  );

  return {
    mrr: activeSubscriptions.reduce(
      (total, customer) => total + (Number(customer.monthly_rate) || 0),
      0
    ),
    activeSubscriptions: activeSubscriptions.length,
    newSubscriptions: customerList.filter((customer) => (
      isOnOrAfter(customer.subscription_created_at, periodStart)
    )).length,
    churnedSubscriptions: customerList.filter((customer) => (
      isOnOrAfter(customer.subscription_cancelled_at, periodStart)
    )).length,
    paymentIssues: paymentIssues.length,
  };
};

const formatCurrency = (amount) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
  maximumFractionDigits: 2,
}).format(amount);

const emptyMetrics = calculateFinanceMetrics([]);

export const FinanceCockpitHome = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const adminUser = useMemo(() => getAdminUser(), []);
  const hasAccess = useCallback(
    (sectionId) => canAccessSection(sectionId, adminUser),
    [adminUser]
  );
  const sections = useMemo(
    () => adminRoutes.finance.sections.filter((section) => hasAccess(section.id)),
    [hasAccess]
  );
  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';
  const activeSection = sections.find((section) => section.path === normalizedPath);
  const [metrics, setMetrics] = useState(emptyMetrics);
  const [metricsAvailable, setMetricsAvailable] = useState(false);
  const [stripeSettings, setStripeSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadWarnings, setLoadWarnings] = useState([]);

  const loadFinanceData = useCallback(async () => {
    setLoading(true);
    setLoadWarnings([]);

    const [customerResult, stripeResult] = await Promise.allSettled([
      customers.getAll(),
      get('/admin/stripe-settings'),
    ]);
    const warnings = [];

    if (customerResult.status === 'fulfilled') {
      setMetrics(calculateFinanceMetrics(customerResult.value));
      setMetricsAvailable(true);
    } else {
      setMetrics(emptyMetrics);
      setMetricsAvailable(false);
      warnings.push('Subscription metrics are unavailable.');
    }

    if (stripeResult.status === 'fulfilled') {
      setStripeSettings(stripeResult.value);
    } else {
      setStripeSettings(null);
      warnings.push('Stripe status is unavailable.');
    }

    setLoadWarnings(warnings);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadFinanceData();
  }, [loadFinanceData]);

  const stripeMode = stripeSettings?.test_mode === false ? 'Live' : 'Test';
  const stripeConfigured = stripeSettings
    ? (stripeSettings.test_mode === false
      ? stripeSettings.live_configured
      : stripeSettings.test_configured)
    : false;
  const displayMetric = (value) => (loading || !metricsAvailable ? '—' : value);

  const kpis = [
    {
      id: 'mrr',
      title: 'Monthly recurring revenue',
      value: displayMetric(formatCurrency(metrics.mrr)),
      subtitle: 'Active subscriptions',
      icon: DollarSign,
    },
    {
      id: 'active-subscriptions',
      title: 'Active subscriptions',
      value: displayMetric(metrics.activeSubscriptions),
      subtitle: 'Currently billing',
      icon: Users,
    },
    {
      id: 'subscription-movement',
      title: 'New / churned',
      value: displayMetric(`${metrics.newSubscriptions} / ${metrics.churnedSubscriptions}`),
      subtitle: `Last ${ACTIVITY_PERIOD_DAYS} days`,
      icon: TrendingUp,
    },
    {
      id: 'payment-issues',
      title: 'Payment issues',
      value: displayMetric(metrics.paymentIssues),
      subtitle: 'Failed or past due',
      icon: AlertTriangle,
      tone: metrics.paymentIssues > 0
        ? 'danger'
        : 'good',
    },
  ];

  const quickActions = [
    {
      id: 'stripe-settings',
      label: 'Open Stripe settings',
      icon: Settings,
      path: STRIPE_SETTINGS_PATH,
      sectionId: 'stripe',
    },
    {
      id: 'view-subscriptions',
      label: 'View subscriptions',
      icon: List,
      path: SUBSCRIPTIONS_PATH,
    },
  ];

  if (!activeSection) {
    return sections[0]
      ? <Navigate to={getRememberedCockpitPath('finance', sections, adminUser)} replace />
      : <Navigate to="/admin" replace />;
  }

  const panelId = `finance-cockpit-tab-panel-${activeSection.id}`;

  return (
    <CockpitHome
      categoryId="finance"
      description="Revenue, subscriptions, and payment health at a glance."
      kpis={kpis}
      quickActions={quickActions}
      canAccessSection={hasAccess}
      showSectionLinks={false}
      alerts={loadWarnings.length > 0 && (
        <Card className="border-amber-300 dark:border-amber-800" role="alert">
          <CardContent className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4">
            <div className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-300">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{loadWarnings.join(' ')}</span>
            </div>
            <Button variant="outline" size="sm" onClick={loadFinanceData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      )}
      overview={(
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="text-base">Billing health</CardTitle>
                {!loading && metricsAvailable && (
                  <Badge variant={metrics.paymentIssues > 0 ? 'destructive' : 'secondary'}>
                    {metrics.paymentIssues > 0
                      ? `${metrics.paymentIssues} ${metrics.paymentIssues === 1 ? 'needs' : 'need'} attention`
                      : 'No issues'}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-stone-600 dark:text-stone-400">
                {loading
                  ? 'Loading subscription health…'
                  : !metricsAvailable
                    ? 'Subscription health could not be loaded.'
                    : metrics.paymentIssues > 0
                      ? 'Review failed or past-due subscriptions and follow up with members.'
                      : 'All tracked subscriptions are clear of payment issues.'}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(SUBSCRIPTIONS_PATH)}
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Review subscriptions
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="text-base">Stripe connection</CardTitle>
                {!loading && stripeSettings && (
                  <Badge variant={stripeConfigured ? 'secondary' : 'destructive'}>
                    {stripeConfigured ? 'Configured' : 'Action needed'}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-stone-600 dark:text-stone-400">
                {loading
                  ? 'Loading Stripe configuration…'
                  : stripeSettings
                    ? `${stripeMode} mode is ${stripeConfigured ? 'configured for billing' : 'missing required API keys'}.`
                    : 'Stripe configuration could not be loaded.'}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(STRIPE_SETTINGS_PATH)}
              >
                <Settings className="h-4 w-4 mr-2" />
                Manage Stripe
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    >
      <CockpitTabs
        tabs={sections}
        active={activeSection.id}
        onChange={(sectionId) => {
          const section = sections.find((candidate) => candidate.id === sectionId);
          if (section) navigate(section.path);
        }}
        label="Finance and billing sections"
        idPrefix="finance-cockpit-tab"
      />

      <CockpitPanel
        id={panelId}
        aria-labelledby={`finance-cockpit-tab-${activeSection.id}`}
      >
        <CockpitWorkspaceContent>
          <Outlet context={{ activeSection, activeSubsection: null }} />
        </CockpitWorkspaceContent>
      </CockpitPanel>
    </CockpitHome>
  );
};

export default FinanceCockpitHome;
